import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Image, ExternalLink, ShoppingBag, Eye, Link2,
  MoreHorizontal, Tag
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const PRODUCT_TYPES_BY_CATEGORY = {
  culinary: [
    { id: "product", label: "Product" },
    { id: "ingredient", label: "Ingredient" },
    { id: "tool", label: "Kitchen Tool" },
    { id: "supply", label: "Supply" },
  ],
  events: [
    { id: "product", label: "Product" },
    { id: "decor", label: "Decor" },
    { id: "supply", label: "Supply" },
    { id: "rental", label: "Rental" },
  ],
  wellness: [
    { id: "product", label: "Product" },
    { id: "tool", label: "Tool" },
    { id: "supplement", label: "Supplement" },
    { id: "supply", label: "Supply" },
  ],
  travel: [
    { id: "product", label: "Product" },
    { id: "gear", label: "Gear" },
    { id: "supply", label: "Supply" },
    { id: "accessory", label: "Accessory" },
  ],
  default: [
    { id: "product", label: "Product" },
    { id: "tool", label: "Tool / Brush" },
    { id: "supply", label: "Supply" },
    { id: "lash_strip", label: "Strip Lashes" },
  ],
};

const EMPTY_STATE_TEXT = {
  culinary: "Add products, ingredients, kitchen tools, or supplies to your directory",
  events: "Add decor items, rentals, supplies, or products to your directory",
  wellness: "Add products, supplements, tools, or supplies to your directory",
  travel: "Add gear, accessories, products, or supplies to your directory",
  default: "Add products, tools, supplies, or accessories to your directory",
};

export default function ProductDirectory({ API, getAuthHeaders, category, services = [] }) {
  const t = useAdminCopy();
  const PRODUCT_TYPES = PRODUCT_TYPES_BY_CATEGORY[category] || PRODUCT_TYPES_BY_CATEGORY.default;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    name: "", brand: "", description: "", image_url: "", price: "",
    is_free: false, product_type: "product", link_url: "", is_public: true,
    linked_services: [], linked_looks: []
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const qs = category ? `?category=${category}` : "";
      const res = await fetch(`${API}/provider/products${qs}`, { headers: getAuthHeaders() });
      if (res.ok) setProducts(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, getAuthHeaders, category]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const resetForm = () => {
    setForm({ name: "", brand: "", description: "", image_url: "", price: "", is_free: false, product_type: "product", link_url: "", is_public: true, linked_services: [], linked_looks: [] });
    setEditing(null);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, brand: p.brand || "", description: p.description || "",
      image_url: p.image_url || "", price: p.price ? String(p.price) : "",
      is_free: p.is_free || false, product_type: p.product_type || "product",
      link_url: p.link_url || "", is_public: p.is_public !== false,
      linked_services: p.linked_services || [], linked_looks: p.linked_looks || []
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    const payload = { ...form, category, price: form.price ? parseFloat(form.price) : null };
    try {
      const url = editing ? `${API}/provider/products/${editing.product_id}` : `${API}/provider/products`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success(editing ? "Product updated" : "Product added");
        setShowDialog(false); resetForm(); fetchProducts();
      } else toast.error("Failed");
    } catch { toast.error("Error"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      const res = await fetch(`${API}/provider/products/${id}`, { method: "DELETE", headers: getAuthHeaders() });
      if (res.ok) { toast.success("Deleted"); fetchProducts(); }
    } catch { toast.error("Error"); }
  };

  const toggleServiceLink = (svcId) => {
    setForm(f => ({
      ...f,
      linked_services: f.linked_services.includes(svcId)
        ? f.linked_services.filter(s => s !== svcId)
        : [...f.linked_services, svcId]
    }));
  };

  const filtered = filter === "all" ? products : products.filter(p => p.product_type === filter);
  const categoryLabel = (category || "").charAt(0).toUpperCase() + (category || "").slice(1);

  return (
    <div className="space-y-4" data-testid="product-directory">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Product Directory
          </h3>
          <p className="text-sm text-gray-500">Products, tools, and supplies: link to services and looks</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} className="gap-2" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Add Product
        </Button>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap">
        {["all", ...PRODUCT_TYPES.map(t => t.id)].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 text-xs rounded-full border transition-all ${filter === f ? "bg-black text-white border-black" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
            {f === "all" ? `All (${products.length})` : PRODUCT_TYPES.find(t => t.id === f)?.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="font-medium text-gray-600">{t("host.products.empty.title", "No products yet")}</p>
            <p className="text-sm text-gray-400 mt-1">{EMPTY_STATE_TEXT[category] || EMPTY_STATE_TEXT.default}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(p => (
            <Card key={p.product_id} className="overflow-hidden hover:shadow-sm transition-shadow group" data-testid={`product-${p.product_id}`}>
              {p.image_url && (
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
              )}
              {!p.image_url && (
                <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center">
                  <Image className="w-8 h-8 text-gray-300" />
                </div>
              )}
              <CardContent className="p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{p.name}</h4>
                    {p.brand && <p className="text-xs text-gray-500">{p.brand}</p>}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100">
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(p)}><Edit2 className="w-3 h-3 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(p.product_id)}><Trash2 className="w-3 h-3 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {p.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.description}</p>}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex gap-1.5">
                    {p.is_free ? (
                      <Badge variant="outline" className="text-xs text-green-600">Free</Badge>
                    ) : p.price ? (
                      <Badge variant="outline" className="text-xs">${p.price}</Badge>
                    ) : null}
                    <Badge variant="secondary" className="text-[10px] capitalize">{(p.product_type || "product").replace("_", " ")}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    {p.link_url && (
                      <a href={`${API}/provider/products/${p.product_id}/track-click`} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-600">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {p.linked_services?.length > 0 && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Link2 className="w-3 h-3" />{p.linked_services.length}</span>
                    )}
                    {!p.is_public && <Eye className="w-3 h-3 text-gray-400" />}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={o => { if (!o) { setShowDialog(false); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name" data-testid="product-name" />
              </div>
              <div>
                <Label>Brand</Label>
                <Input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="e.g., MAC, Dyson" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What is this product and when do you use it?" />
            </div>
            <div>
              <Label>Product Image</Label>
              <div className="flex items-center gap-2 mt-1">
                {form.image_url && <img src={form.image_url} alt="preview" className="h-16 w-16 object-cover rounded-lg border" />}
                <label className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 text-sm">
                  <Image className="w-4 h-4 text-gray-500" />
                  {form.image_url ? "Change Image" : "Upload Image"}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    try {
                      const res = await fetch(`${API}/upload/image`, { method: "POST", headers: { Authorization: getAuthHeaders().Authorization }, body: fd });
                      if (res.ok) { const d = await res.json(); setForm(f => ({ ...f, image_url: d.url || d.image_url })); toast.success("Image uploaded"); }
                      else toast.error("Upload failed");
                    } catch { toast.error("Upload error"); }
                  }} />
                </label>
                {form.image_url && <button onClick={() => setForm(f => ({ ...f, image_url: "" }))} className="text-xs text-red-500 hover:underline">Remove</button>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Type</Label>
                <select value={form.product_type} onChange={e => setForm(f => ({ ...f, product_type: e.target.value }))} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="product-type">
                  {PRODUCT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Price ($)</Label>
                <Input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" disabled={form.is_free} />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={form.is_free} onCheckedChange={v => setForm(f => ({ ...f, is_free: v, price: v ? "" : f.price }))} />
                  Free
                </label>
              </div>
            </div>
            <div>
              <Label>Purchase Link (trackable)</Label>
              <Input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} placeholder="https://amazon.com/..." />
              <p className="text-xs text-gray-400 mt-1">Clicks are tracked automatically</p>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div><p className="text-sm font-medium">Public</p><p className="text-xs text-gray-500">Visible to clients in their preference card</p></div>
              <Switch checked={form.is_public} onCheckedChange={v => setForm(f => ({ ...f, is_public: v }))} />
            </div>

            {/* Link to services */}
            {services.length > 0 && (
              <div>
                <Label>Link to Services</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {services.map(s => (
                    <button key={s.service_id} onClick={() => toggleServiceLink(s.service_id)}
                      className={`px-2 py-1 text-xs border rounded-lg transition-all ${form.linked_services.includes(s.service_id) ? "bg-black text-white border-black" : "border-gray-200"}`}>
                      <Tag className="w-3 h-3 inline mr-1" />{s.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">{saving ? "Saving..." : editing ? "Save" : "Add Product"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
